import { Box, CircularProgress, Typography } from "@mui/joy";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { rootRoute } from "app";
import { Form } from "features/form";
import React, { useEffect, useState } from "react";
import { tenantService } from "shared/api/tenant.service";
import { ErrorBoundary } from "shared/hoc";
import { useAuthState } from "shared/hook";
import {
  type ContactRole,
  useOnboardingStore,
  useUserStore
} from "shared/store";
import {
  DEFAULT_NAVIGATION_TARGET,
  isCompletedStatus,
  isDomainComplete,
  isWorkdayComplete
} from "shared/utils/onboardingStatus";
import styles from "./Home.module.scss";

interface User {
  email: string;
  displayName: string;
  uuid: string;
  roles?: string[];
}

const Home: React.FC = () => {
  const { loading, user } = useAuthState();
  const [onboardingUser, setOnboardingUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);
  // Track whether session restoration has completed to prevent race conditions
  // Navigation effect must wait for this to be true before making decisions
  const [sessionRestored, setSessionRestored] = useState(false);
  // Track login in progress to prevent "Access Restricted" flash during state transition
  // This covers the gap between Firebase auth completing and onboardingUser being set
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const navigate = useNavigate();
  const { setUuid, setTenantId } = useOnboardingStore();

  // Restore onboardingUser from sessionStorage if Firebase user exists but component state is null
  // This handles the case where Firebase auth is restored on app mount but component state wasn't
  //
  // SECURITY: This restoration includes session binding checks to prevent:
  // - Session fixation attacks (attacker injecting crafted session data)
  // - Token reuse after logout (old tokens with different permissions)
  // - Cross-user session pollution on shared devices
  useEffect(() => {
    if (user && !onboardingUser && !loading) {
      const storedUser = sessionStorage.getItem("onboardingUser");
      const storedToken = sessionStorage.getItem("onboardingToken");

      if (storedUser && storedToken) {
        try {
          const parsedUser = JSON.parse(storedUser) as User;

          // Validate required fields to ensure data structure is correct
          if (!parsedUser.email || !parsedUser.uuid) {
            throw new Error(
              "Invalid user data structure: missing email or uuid"
            );
          }

          // SECURITY: Session binding validation (case-insensitive per RFC 5321)
          // Verify the Firebase user's email matches the stored onboarding user's email
          // This prevents session fixation attacks where an attacker injects
          // a different user's session data into sessionStorage
          //
          // CRITICAL: Both emails MUST be present and non-empty for validation.
          // If either is missing, reject the session to prevent bypass attacks where
          // an attacker crafts sessionStorage with empty email to skip validation.
          if (!user.email || !parsedUser.email) {
            throw new Error(
              "Invalid session: email required for session validation"
            );
          }

          // NOTE: Email comparison MUST be case-insensitive - attackers could bypass
          // case-sensitive checks using differently-cased versions of the same email
          if (parsedUser.email.toLowerCase() !== user.email.toLowerCase()) {
            throw new Error(
              "Session binding failed - possible session fixation attempt"
            );
          }

          setOnboardingUser(parsedUser);
          setAuthToken(storedToken);

          // CRITICAL: Restore roles to Zustand store for role-based navigation
          // This ensures components like domain.tsx can check user roles correctly
          if (parsedUser.roles && Array.isArray(parsedUser.roles)) {
            useUserStore.getState().setRoles(parsedUser.roles as ContactRole[]);
          }
        } catch {
          // SECURITY: Clear ALL session data on any validation failure
          // This prevents partial session restoration that could lead to
          // inconsistent state or security vulnerabilities
          clearAllSessionData();
        }
      }
      // Mark session restoration as complete - user data was found and processed
      setSessionRestored(true);
    } else if (!loading) {
      // No Firebase user or onboardingUser already exists - nothing to restore
      // Mark as complete so navigation effect can proceed
      setSessionRestored(true);
    }
  }, [user, onboardingUser, loading]);

  /**
   * Clears all onboarding-related session data.
   * Called on logout, validation failures, or security violations.
   * SECURITY: Always clear all data together to prevent partial session attacks.
   */
  const clearAllSessionData = () => {
    sessionStorage.removeItem("onboardingUser");
    sessionStorage.removeItem("onboardingToken");
    sessionStorage.removeItem("onboardingData");
  };

  useEffect(() => {
    // GUARD: Prevent race condition with session restoration
    // Don't process navigation until BOTH conditions are met:
    // 1. Firebase auth loading is complete (loading === false)
    // 2. Session restoration effect has finished (sessionRestored === true)
    // This ensures the session restoration effect (above) has had a chance to
    // restore onboardingUser from sessionStorage before we make navigation decisions
    if (loading || !sessionRestored) return;

    if (!onboardingUser || !authToken || isCheckingOnboarding) return;

    const processOnboardingAndNavigate = async () => {
      if (!onboardingUser?.uuid) {
        console.error("No UUID found for onboarding user");
        navigate({ to: "/onboarding/workday" });
        return;
      }

      setIsCheckingOnboarding(true);

      try {
        // Store UUID in the store
        setUuid(onboardingUser.uuid);

        // Always fetch onboarding status from hub-api (single source of truth)
        const statusResponse = await tenantService.getOnboardingStatus(
          onboardingUser.uuid
        );

        if (statusResponse.success && statusResponse.data) {
          const { status, tenantId, onboardingData } = statusResponse.data;

          // Store tenantId if available
          if (tenantId) {
            setTenantId(tenantId);
          }

          // If there's existing onboarding data from the API, load it into sessionStorage
          if (onboardingData && Object.keys(onboardingData).length > 0) {
            const dataToStore = {
              ...onboardingData,
              ...(tenantId && !onboardingData.tenantId && { tenantId })
            };
            sessionStorage.setItem(
              "onboardingData",
              JSON.stringify(dataToStore)
            );
          } else {
            // No onboarding data from API - clear sessionStorage for fresh start
            sessionStorage.removeItem("onboardingData");
          }

          // Navigate based on the current stage from API
          switch (status) {
            case "WORKDAY_CONFIGURED":
              navigate({ to: "/onboarding/domain" });
              break;
            case "DOMAIN_CONFIGURED":
              navigate({ to: "/onboarding/sso" });
              break;
            case "SSO_CONFIGURED":
            case "IMPL_COMPLETED":
              navigate({ to: "/onboarding/completion" });
              break;
            default:
              navigate({ to: "/onboarding/workday" });
              break;
          }
        } else {
          // No valid status response - start from workday
          sessionStorage.removeItem("onboardingData");
          navigate({ to: "/onboarding/workday" });
        }
      } catch (error) {
        console.error("Error processing onboarding status:", error);
        // On error, start from Workday configuration page
        setUuid(onboardingUser.uuid);
        sessionStorage.removeItem("onboardingData");
        navigate({ to: "/onboarding/workday" });
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    processOnboardingAndNavigate();
  }, [
    loading,
    sessionRestored,
    onboardingUser,
    authToken,
    isCheckingOnboarding,
    navigate,
    setUuid,
    setTenantId
  ]);

  // Called before Firebase auth to prevent "Access Restricted" flash
  const handleLoginStarted = () => {
    setIsLoggingIn(true);
  };

  // Called when login fails to reset the loading state
  const handleLoginFailed = () => {
    setIsLoggingIn(false);
  };

  const handleLoginSuccess = (user: User, token: string) => {
    setOnboardingUser(user);
    setAuthToken(token);
    // Store in sessionStorage for persistence across page refreshes
    sessionStorage.setItem("onboardingUser", JSON.stringify(user));
    sessionStorage.setItem("onboardingToken", token);
    // Clear login-in-progress flag now that state is fully set
    setIsLoggingIn(false);
  };

  return (
    <>
      {isCheckingOnboarding || isLoggingIn ? (
        // Show loading spinner during:
        // 1. isCheckingOnboarding: Fetching onboarding status from API
        // 2. isLoggingIn: Transitional state between Firebase auth and state update
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2
          }}
        >
          <CircularProgress
            aria-describedby="loading-message"
            aria-label="Loading progress indicator"
          />
          <Typography level="body-md" id="loading-message">
            {isLoggingIn
              ? "Signing you in..."
              : "Checking your onboarding status..."}
          </Typography>
        </Box>
      ) : !loading && !user && !onboardingUser ? (
        <>
          <title>Onboarding Login</title>
          <ErrorBoundary type="page">
            <div className={styles.main_container}>
              <div className={styles.main_wrapper}>
                <ErrorBoundary type="notification">
                  <Form.EmailPasswordLogin
                    onLoginSuccess={handleLoginSuccess}
                    onLoginStarted={handleLoginStarted}
                    onLoginFailed={handleLoginFailed}
                  />
                </ErrorBoundary>
              </div>
            </div>
          </ErrorBoundary>
        </>
      ) : !loading && user && !onboardingUser ? (
        // Regular Firebase user is logged in but not as onboarding user
        // This is an invalid state for the onboarding flow
        <Box
          role="alert"
          aria-live="assertive"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            gap: 2,
            p: 3,
            textAlign: "center"
          }}
        >
          <Typography level="h3" component="h1">
            Access Restricted
          </Typography>
          <Typography level="body-md" sx={{ maxWidth: 400 }}>
            This page is for customer onboarding. Please use the onboarding
            credentials provided in your welcome email.
          </Typography>
        </Box>
      ) : null}
    </>
  );
};

export default Home;

interface LoginSearch {
  uuid?: string;
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Home,
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      uuid: typeof search.uuid === "string" ? search.uuid : undefined
    };
  }
});
